import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { products } from '../data/products';
import { categories } from '../data/categories';
import { company } from '../data/company';
import { downloadFile } from '../utils/utils';
import type { Product } from '../types/products';
import PageHero from '../components/PageHero';
import ProductCard from '../components/ProductCard';
import SpecTable from '../components/SpecTable';
import Modal from '../components/Modal';
import Icon from '../components/Icon';

const ALL = 'all';

export default function ProductFinder() {
  const [query, setQuery] = useState('');
  const [categorySlug, setCategorySlug] = useState<string>(ALL);
  const [group, setGroup] = useState<string>(ALL);
  const [active, setActive] = useState<Product | null>(null);

  const groupOptions = useMemo(() => {
    const source = categorySlug === ALL ? products : products.filter((p) => p.categorySlug === categorySlug);
    return Array.from(new Set(source.map((p) => p.group)));
  }, [categorySlug]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => {
      const matchesCategory = categorySlug === ALL || p.categorySlug === categorySlug;
      const matchesGroup = group === ALL || p.group === group;
      const matchesQuery =
        !q ||
        p.name.toLowerCase().includes(q) ||
        p.code.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.applications.some((a) => a.toLowerCase().includes(q)) ||
        p.keyFeatures.some((f) => f.toLowerCase().includes(q));
      return matchesCategory && matchesGroup && matchesQuery;
    });
  }, [query, categorySlug, group]);

  const resetFilters = () => {
    setQuery('');
    setCategorySlug(ALL);
    setGroup(ALL);
  };

  return (
    <div>
      <PageHero
        eyebrow="Product Finder"
        title="Find the right chemistry, fast"
        description="Search and filter our full catalogue by product family, group and application to zero in on the grade you need."
      />

      <section className="container-page py-10">
        {/* Search + filters */}
        <div className="card p-5 sm:p-6">
          <div className="grid gap-4 md:grid-cols-[1.6fr_1fr_1fr]">
            <div className="relative">
              <Icon name="search" className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-text-secondary" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, code or application…"
                className="w-full rounded-xl border border-ink/10 bg-background py-3 pl-12 pr-4 text-sm text-text-primary outline-none transition-colors placeholder:text-text-secondary/70 focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <select
              value={categorySlug}
              onChange={(e) => {
                setCategorySlug(e.target.value);
                setGroup(ALL);
              }}
              className="w-full rounded-xl border border-ink/10 bg-background px-4 py-3 text-sm font-medium text-text-primary outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            >
              <option value={ALL}>All product families</option>
              {categories.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.name}
                </option>
              ))}
            </select>
            <select
              value={group}
              onChange={(e) => setGroup(e.target.value)}
              className="w-full rounded-xl border border-ink/10 bg-background px-4 py-3 text-sm font-medium text-text-primary outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            >
              <option value={ALL}>All groups</option>
              {groupOptions.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <p className="text-sm text-text-secondary">
              <span className="font-semibold text-text-primary">{results.length}</span> product
              {results.length === 1 ? '' : 's'} found
            </p>
            {(query || categorySlug !== ALL || group !== ALL) && (
              <button
                type="button"
                onClick={resetFilters}
                className="text-sm font-semibold text-primary hover:text-primary-dark"
              >
                Clear filters
              </button>
            )}
            <button
              type="button"
              onClick={() => downloadFile(company.productGuide.fileUrl, company.productGuide.fileName)}
              className="ml-auto inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary-dark"
            >
              <Icon name="download" className="h-4 w-4" /> Full product guide
            </button>
          </div>
        </div>

        {/* Results */}
        {results.length > 0 ? (
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {results.map((p) => (
              <ProductCard key={p.id} product={p} onQuickView={setActive} />
            ))}
          </div>
        ) : (
          <div className="mt-8 rounded-2xl border border-dashed border-ink/15 p-12 text-center">
            <Icon name="search" className="mx-auto h-10 w-10 text-text-secondary/50" />
            <p className="mt-4 font-semibold text-text-primary">No products match your filters</p>
            <p className="mt-1 text-sm text-text-secondary">Try a different search term or clear the filters.</p>
            <button type="button" onClick={resetFilters} className="btn-outline mt-5">
              Reset search
            </button>
          </div>
        )}
      </section>

      {/* Quick-view modal */}
      <Modal isOpen={!!active} onClose={() => setActive(null)} title={active?.name ?? ''}>
        {active && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-primary/8 px-2.5 py-1 font-mono text-xs font-semibold text-primary">
                {active.code}
              </span>
              <span className="text-xs font-medium text-secondary-dark">{active.group}</span>
            </div>
            <p className="text-sm leading-6 text-text-secondary">{active.description}</p>

            <div>
              <h4 className="mb-2 text-sm font-bold text-text-primary">Key Features</h4>
              <ul className="grid gap-2 sm:grid-cols-2">
                {active.keyFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-text-secondary">
                    <Icon name="check" className="mt-0.5 h-4 w-4 flex-none text-primary" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="mb-2 text-sm font-bold text-text-primary">Typical Properties</h4>
              <SpecTable rows={active.specTable ?? active.typicalProperties} />
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              <Link to={`/products/${active.categorySlug}/${active.id}`} className="btn-primary">
                Full details <Icon name="arrow" className="h-4 w-4" />
              </Link>
              <button
                type="button"
                onClick={() => downloadFile(company.productGuide.fileUrl, company.productGuide.fileName)}
                className="btn-outline"
              >
                <Icon name="download" className="h-4 w-4" /> Product Guide
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
